'use client';

import { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { readConsent } from '@/lib/analytics/consent';
import { trackFunnelEvent, FUNNEL_EVENTS } from '@/lib/analytics/events';

const WHATSAPP_NUMBER = '27774584896';
const WHATSAPP_MESSAGE = "Hi Zamzi Connect, I'd like to get my business listed.";

/** Routes where a sales CTA is noise rather than help. */
const HIDDEN_PREFIXES = [
  '/dashboard',
  '/admin',
  '/agent',
  '/auth',
  '/payment',
  '/paywall',
  '/settings',
  '/profile/edit',
];

/**
 * Floating WhatsApp CTA.
 *
 * Rationale: in South Africa WhatsApp is close to universal on mobile, and a
 * business owner who is not ready to commit to a form will instead ask a
 * question in a chat. That conversation converts far better than a cold form,
 * but before this existed the only WhatsApp link on the site was in the footer —
 * effectively invisible on a phone.
 *
 * Two implementation details that matter:
 *
 * - It waits for the consent decision before rendering. That keeps it from
 *   sitting on top of the cookie banner, and it is the same decision point the
 *   analytics provider uses.
 * - It is suppressed on app routes, where it would just be in the way.
 */
export function FloatingWhatsAppButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (readConsent() === 'unknown') return;

    const path = window.location.pathname;
    if (HIDDEN_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
      return;
    }

    const onScroll = () => setVisible(window.scrollY > 240);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackFunnelEvent(FUNNEL_EVENTS.whatsappClick, { cta: 'floating_bubble' })}
      aria-label="Chat with Zamzi Connect on WhatsApp"
      className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_8px_24px_-6px_rgba(0,0,0,0.45)] transition-transform duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 motion-reduce:transition-none sm:bottom-6 sm:right-6"
    >
      <MessageCircle aria-hidden="true" className="h-7 w-7" />
    </a>
  );
}
